import { ComponentType } from '@angular/cdk/portal';
import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { InspectionTypes } from '@core/enums/inspection-types';
import { FileInfo } from '@core/interfaces/file-info';
import { VisualEventModalInput } from '@core/interfaces/modal-inputs/visual-event-modal-input';
import { VETableColumn } from '@core/interfaces/report-template/visual-event-table-column';
import { VisualEvent } from '@core/interfaces/visual-event';
import { firstValueFrom } from 'rxjs';
import { FilesService } from '@web/services/files.service';
import { TaskFilesService } from '@web/services/task-files.service';
import { TranslateModule } from '@ngx-translate/core';
import { DialogService } from 'osc-reference-app-common';
import { NgOptimizedImage } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { VisualEventImageViewerComponent } from '../visual-event-image-viewer/visual-event-image-viewer.component';

@Component({
  selector: 'app-visual-event-modal',
  templateUrl: './visual-event-modal.component.html',
  imports: [
    NgOptimizedImage,
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    FormsModule,
    TranslateModule,
  ],
})
export class VisualEventModalComponent implements OnInit {
  public data = inject<VisualEventModalInput>(MAT_DIALOG_DATA);
  public selectedVisualEvent: VisualEvent;
  public inspectionType: InspectionTypes;
  public InspectionTypes = InspectionTypes;

  public visualEventTypes: Array<string> = [];
  public visualEventStatus: Array<VETableColumn> = [];
  public priorityCodes: Array<{ code: string; label: string }> = [];

  #dialogService = inject(DialogService);
  #taskFilesService = inject(TaskFilesService);
  #filesService = inject(FilesService);
  #projectId: string;
  #jobId: string;
  #taskId: string;

  public ngOnInit() {
    this.selectedVisualEvent = this.data.visualEvent;
    this.#projectId = this.data.projectId;
    this.#jobId = this.data.jobId;
    this.#taskId = this.data.taskId;
    this.inspectionType = this.data.inspectionType;
    this.getVisualEventTypes();
  }

  public getVisualEventTypes(): void {
    this.#taskFilesService.fetchTaskFiles(this.#projectId, this.#jobId, this.#taskId).subscribe(async taskFiles => {
      const report: FileInfo = taskFiles.inspectionReport;
      if (report && report.downloadUri) {
        const template: any = await firstValueFrom(this.#filesService.fetchFileContent(report));
        if (template.list && template.list.length > 0) {
          this.visualEventTypes = [
            ...this.visualEventTypes,
            ...template.list
              .filter(x => x.id === 'VEList')
              .map(x => x.listData)
              .flat(),
          ];
        }
        template.bodies.forEach(page => {
          page.tables.forEach(table => {
            if (table && table.sectionStageTag === 'VEList' && table.tableType === 'surveyTable') {
              this.visualEventTypes = [
                ...this.visualEventTypes,
                ...table.rowList.filter(row => row.tagging === 'VisualEventTypeItem').map(row => row.label),
              ];
              this.visualEventStatus = [...this.visualEventStatus, ...table.columnList.filter(column => column.tagging === 'StatusItem')];
              if (table.priorityList && table.priorityList.length > 0) {
                this.priorityCodes = [...this.priorityCodes, ...table.priorityList];
              }
            }
          });
        });
        if (template.bodies.length === 0 && this.visualEventStatus.length === 0) {
          this.visualEventStatus = [
            {
              label: 'Acceptable',
              width: 10,
              inputType: 'radio',
              textOrientation: 'vertical',
              textAlignment: 'center',
            },
            {
              label: 'Non acceptable',
              width: 10,
              inputType: 'radio',
              textOrientation: 'vertical',
              textAlignment: 'center',
            },
            {
              label: 'Non applicable',
              width: 10,
              inputType: 'radio',
              textOrientation: 'vertical',
              textAlignment: 'center',
            },
          ];
        }
        this.visualEventStatus = this.visualEventStatus.filter((v, i, a) => a.findIndex(x => x.label === v.label) === i);
        this.priorityCodes = this.priorityCodes.filter((v, i, a) => a.findIndex(x => x.code === v.code) === i);
      }
    });
  }

  public openImage(url: string): void {
    this.#dialogService.openXlDialog(VisualEventImageViewerComponent as ComponentType<VisualEventImageViewerComponent>, {
      url,
    });
  }

  public isPriorityCodeRequired(): boolean {
    if (
      !this.selectedVisualEvent.isPhotoOnlyEvent &&
      this.inspectionType !== InspectionTypes.UT &&
      this.inspectionType !== InspectionTypes.UT_Baseline
    ) {
      const statusColumn = this.visualEventStatus.find(i => i.label === this.selectedVisualEvent.visualEventStatus);
      return statusColumn ? statusColumn.needsPriority : false;
    } else {
      return false;
    }
  }
}
